import { useGate, useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { appealModel } from '~/entities/common/Balance/Appeal'
import {
  balanceHistoryModel,
  Form,
} from '~/entities/common/Balance/BalanceHistory'
import { DriverOptionForTransaction } from '~/shared/api'
import {
  transactionBalanceTypeEnumLabels,
  transactionMoveFundsTypeEnumLabels,
  transactionPaymentTypeEnumLabels,
} from '~/shared/config/constants'
import {
  TransactionBalanceTypeEnum,
  TransactionMoveFundsTypeEnum,
  TransactionPaymentTypeEnum,
} from '~/shared/config/enums'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageTitle } from '~/shared/ui/Layout'

type AppealCreateTransactionProps = {
  moveFundsType: TransactionMoveFundsTypeEnum
}

export function AppealCreateTransaction({
  moveFundsType,
}: AppealCreateTransactionProps) {
  const { t } = useTranslation()
  const { appealId } = useParams<{ appealId: string }>()

  useGate(appealModel.Gate, { appealId })

  const isSubmitting = useStore(balanceHistoryModel.createFx.pending)
  const isLoading = useStore(appealModel.requestFx.pending)

  const defaultValues = useStoreMap(appealModel.$appeal, (appeal) => {
    const driver = appeal?.getDriver()
    return {
      ...appeal?.getAttributes(),
      driverOption: driver
        ? {
            ...(appeal
              ?.getDriver()
              ?.getDriverOptionForTransaction() as DriverOptionForTransaction),
            carOption: appeal?.getCar()?.getOption(),
          }
        : null,
      appealId,
      amount: appeal?.getAmount(),
      moveFundsType: {
        id: moveFundsType,
        label: transactionMoveFundsTypeEnumLabels[moveFundsType],
      },
      paymentType: {
        id: TransactionPaymentTypeEnum.NON_CASH,
        label:
          transactionPaymentTypeEnumLabels[TransactionPaymentTypeEnum.NON_CASH],
      },
      balanceType: {
        id: TransactionBalanceTypeEnum.MAIN,
        label:
          transactionBalanceTypeEnumLabels[TransactionBalanceTypeEnum.MAIN],
      },
    }
  })
  const appeal = useStore(appealModel.$appeal)

  const title = useMemo(
    () =>
      appeal
        ? t('appeal number from createAt', {
            number: appeal?.getNumber(),
            createAt: formatDateTimeForUI(appeal?.getCreatedAt() as string),
          })
        : '...',
    [appeal, t],
  )

  const breadcrumbs: BreadcrumbsList = [
    {
      titleI18n: 'balances',
      link: '/balance/balance-histories',
    },
    {
      titleI18n: 'requests',
      link: '/balance/appeals',
    },
    {
      title: title,
      link: `/balance/appeals/${appealId}`,
    },
    {
      titleI18n: 'transaction creation',
    },
  ]

  return (
    <>
      <Container isHeader>
        <Breadcrumbs list={breadcrumbs} />
        <PageTitle>{t('transaction creation')}</PageTitle>
      </Container>
      <Form
        defaultValues={defaultValues}
        isReadOnlyDriver
        isReadOnlyCar
        onSuccess={balanceHistoryModel.formSubmitted}
        formWatchOption={{
          flowStatement: { autoSet: true, separator: ';' },
        }}
        isCreating
        isSubmitting={isSubmitting}
        isLoading={isLoading}
      />
    </>
  )
}
